/**
 * HTTP client implementation using Axios
 */

import axios, { type AxiosInstance, type AxiosResponse, type AxiosError, isAxiosError } from 'axios'
import type { RequestConfig, ApiResponse, ApiError } from '../types'

/**
 * HTTP client for making requests using Axios
 */
export class HttpClient {
  private axiosInstance: AxiosInstance

  constructor(config: Partial<RequestConfig> = {}) {
    // Create Axios instance with default configuration
    const axiosConfig: any = {
      timeout: config.timeout || 30000,
      responseType:
        (config.responseType === 'arrayBuffer' ? 'arraybuffer' : config.responseType) || 'json',
      validateStatus: config.validateStatus || (status => status >= 200 && status < 300),
    }

    // Add optional properties only if they exist
    if (config.baseURL) axiosConfig.baseURL = config.baseURL
    if (config.headers) axiosConfig.headers = config.headers
    if (config.params) axiosConfig.params = config.params
    if (config.withCredentials !== undefined) axiosConfig.withCredentials = config.withCredentials
    if (config.maxRedirects !== undefined) axiosConfig.maxRedirects = config.maxRedirects
    if (config.signal) axiosConfig.signal = config.signal

    this.axiosInstance = axios.create(axiosConfig)
  }

  /**
   * Transform Axios response to our ApiResponse format
   */
  private transformAxiosResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
      config: response.config as unknown as RequestConfig,
      request: response.request,
    }
  }

  /**
   * Transform Axios error to our ApiError format
   */
  private transformAxiosError(error: AxiosError): ApiError {
    const apiError: ApiError = {
      name: 'ApiError',
      message: error.message,
      config: error.config as unknown as RequestConfig,
      request: error.request,
      response: error.response ? this.transformAxiosResponse(error.response) : undefined,
      code: error.code || 'UNKNOWN_ERROR',
      isAxiosError: true,
      toJSON: () => ({
        name: 'ApiError',
        message: error.message,
        code: error.code || 'UNKNOWN_ERROR',
        status: error.response?.status,
        config: error.config as unknown as RequestConfig,
        stack: (error as Error).stack,
      }),
    } as ApiError

    // Copy Error prototype methods
    Object.setPrototypeOf(apiError, Error.prototype)

    return apiError
  }

  /**
   * Makes an HTTP request using Axios
   */
  async request<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    try {
      // Transform our config to Axios config
      const axiosConfig: any = { ...config }

      // Handle responseType mapping
      if (config.responseType === 'arrayBuffer') {
        axiosConfig.responseType = 'arraybuffer'
      }

      const response = await this.axiosInstance.request<T>(axiosConfig)
      return this.transformAxiosResponse<T>(response)
    } catch (error) {
      if (isAxiosError(error)) {
        throw this.transformAxiosError(error)
      }
      throw error
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'GET' })
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'POST', data })
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PUT', data })
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PATCH', data })
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'DELETE' })
  }

  /**
   * HEAD request
   */
  async head<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'HEAD' })
  }

  /**
   * OPTIONS request
   */
  async options<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'OPTIONS' })
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(
    onFulfilled?: (config: any) => any | Promise<any>,
    onRejected?: (error: any) => any
  ) {
    return this.axiosInstance.interceptors.request.use(onFulfilled, onRejected)
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(
    onFulfilled?: (response: AxiosResponse) => any | Promise<any>,
    onRejected?: (error: any) => any
  ) {
    return this.axiosInstance.interceptors.response.use(onFulfilled, onRejected)
  }

  /**
   * Remove request interceptor
   */
  removeRequestInterceptor(interceptorId: number) {
    this.axiosInstance.interceptors.request.eject(interceptorId)
  }

  /**
   * Remove response interceptor
   */
  removeResponseInterceptor(interceptorId: number) {
    this.axiosInstance.interceptors.response.eject(interceptorId)
  }

  /**
   * Get the underlying Axios instance
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance
  }
}
